import type { RealtimeEvent, RealtimeTopic } from './realtime/events'

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.ohear.ai'
const VERSION_OVERRIDE = import.meta.env.VITE_CF_WORKER_VERSION_OVERRIDE as string | undefined
const POLL_MS = 10_000
const MAX_SEEN = 1_000

interface EventRow extends RealtimeEvent {
  id: string
}

interface Feed {
  cursorAt: string
  cursorId: string
  listeners: Set<(event: EventRow) => void>
  seen: Set<string>
  timer?: number
  polling: boolean
}

const feeds = new Map<string, Feed>()

function topicFor(eventType: string): RealtimeTopic {
  const value = eventType.toLowerCase()
  if (value.includes('comment')) return 'comments'
  if (value.includes('project') || value.includes('episode') || value.includes('stem')) {
    return 'project_progress'
  }
  if (value.includes('take') || value.includes('batch') || value.includes('pickup')) return 'takes'
  return 'notifications'
}

async function poll(orgId: string, feed: Feed): Promise<void> {
  if (feed.polling || document.visibilityState === 'hidden') return
  feed.polling = true
  try {
    const response = await fetch(
      `${API_BASE}/api/v1/events?after=${encodeURIComponent(feed.cursorAt)}&after_id=${encodeURIComponent(feed.cursorId)}&limit=100`,
      {
        credentials: 'include',
        cache: 'no-store',
        headers: VERSION_OVERRIDE
          ? { 'Cloudflare-Workers-Version-Overrides': `ohear-api="${VERSION_OVERRIDE}"` }
          : undefined,
      },
    )
    if (!response.ok) return
    const body = (await response.json()) as { items?: EventRow[] }
    for (const event of body.items ?? []) {
      if (feed.seen.has(event.id)) continue
      feed.seen.add(event.id)
      feed.cursorAt = event.emitted_at
      feed.cursorId = event.id
      for (const listener of feed.listeners) listener(event)
    }
    while (feed.seen.size > MAX_SEEN) {
      const first = feed.seen.values().next().value as string | undefined
      if (!first) break
      feed.seen.delete(first)
    }
  } catch {
    // Network changes are expected on recording laptops; the next tick retries.
  } finally {
    feed.polling = false
  }
}

export function subscribeCloudflareEvents(
  orgId: string,
  topic: RealtimeTopic | null,
  listener: (event: EventRow) => void,
): () => void {
  let feed = feeds.get(orgId)
  if (!feed) {
    feed = {
      cursorAt: new Date().toISOString(),
      cursorId: '00000000-0000-0000-0000-000000000000',
      listeners: new Set(),
      seen: new Set(),
      polling: false,
    }
    feeds.set(orgId, feed)
  }
  const filtered = (event: EventRow) => {
    if (!topic || topicFor(event.event_type) === topic) listener(event)
  }
  feed.listeners.add(filtered)
  if (feed.timer === undefined) {
    void poll(orgId, feed)
    feed.timer = window.setInterval(() => void poll(orgId, feed!), POLL_MS)
  }

  return () => {
    feed?.listeners.delete(filtered)
    if (feed && feed.listeners.size === 0) {
      if (feed.timer !== undefined) window.clearInterval(feed.timer)
      feeds.delete(orgId)
    }
  }
}
